import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import OdooSyncBadge from './OdooSyncBadge';

/**
 * Drawer section showing the assigned departments as chips + a typeahead
 * combobox to add new ones.
 *
 * Props:
 *   user                   selected user
 *   userDepartments        [{id, department_id, department_name?}]  current assignments
 *   departments            full list of {id, name}
 *   onToggleDepartment     (deptId) => Promise<void>  (parent owns the call)
 *   readOnly               when true, hides add/remove controls (viewer lacks
 *                           the departments.members permission)
 */
export default function UserDepartmentsSection({
  user,
  userDepartments,
  departments,
  onToggleDepartment,
  readOnly = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [previewDept, setPreviewDept] = useState(null);
  const [defaultsForPreview, setDefaultsForPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const assignedIds = useMemo(
    () => new Set(userDepartments.map((ud) => ud.department_id)),
    [userDepartments]
  );

  const sourceByDeptId = useMemo(() => {
    const map = new Map();
    for (const ud of userDepartments) {
      map.set(ud.department_id, ud.source || 'membership');
    }
    return map;
  }, [userDepartments]);

  const assignedList = useMemo(
    () => departments
      .filter((d) => assignedIds.has(d.id))
      .map((d) => ({ ...d, source: sourceByDeptId.get(d.id) || 'membership' })),
    [departments, assignedIds, sourceByDeptId]
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return departments
      .filter((d) => !assignedIds.has(d.id))
      .filter((d) => (q ? d.name.toLowerCase().includes(q) : true))
      .slice(0, 8);
  }, [departments, assignedIds, query]);

  const openPreview = async (dept) => {
    setPreviewDept(dept);
    setDefaultsForPreview(null);
    try {
      const r = await axios.get(`${API_BASE_URL}/api/department_tools`, {
        params: { department_id: dept.id, detailed: 1 },
        withCredentials: true,
      });
      const rows = Array.isArray(r.data) ? r.data : [];
      setDefaultsForPreview(rows.filter((x) => x.visibility === 'all').length);
    } catch {
      setDefaultsForPreview(null);
    }
  };

  const cancelPreview = () => {
    setPreviewDept(null);
    setDefaultsForPreview(null);
  };

  const commitAdd = async () => {
    if (!previewDept) return;
    setBusy(true);
    try {
      await onToggleDepartment(previewDept.id);
      cancelPreview();
      setQuery('');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const removeChip = async (deptId) => {
    if (!window.confirm('Remove this department from the user?')) return;
    setBusy(true);
    try {
      await onToggleDepartment(deptId);
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <section className="ut-depts" aria-labelledby={`ut-depts-${user.id}`}>
      <div className="ut-depts-head">
        <h4 id={`ut-depts-${user.id}`}>Departments</h4>
        <span className="ut-depts-head-right">
          <OdooSyncBadge status={user.odoo_sync_status} />
          <span className="ut-depts-count">{assignedList.length} assigned</span>
        </span>
      </div>

      <div className="ut-depts-chips">
        {assignedList.length === 0 && (
          <span className="ut-depts-empty">No departments yet.</span>
        )}
        {assignedList.map((d) => (
          <span
            key={d.id}
            className={`ut-depts-chip${d.source === 'manager' ? ' ut-depts-chip--manager' : ''}`}
            title={d.source === 'manager' ? `Manager of ${d.name} — remove from Department Managers to unassign` : undefined}
          >
            <span>{d.name}</span>
            {d.source === 'manager' && (
              <span className="ut-depts-chip-tag" aria-label="Source: department manager">Manager</span>
            )}
            {d.source !== 'manager' && !readOnly && (
              <button
                type="button"
                className="ut-depts-chip-x"
                aria-label={`Remove ${d.name}`}
                onClick={() => removeChip(d.id)}
                disabled={busy}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>

      {!readOnly && (
        <div className="ut-depts-combo" ref={wrapRef}>
          <input
            ref={inputRef}
            type="search"
            placeholder="Add department…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            aria-label="Search departments to add"
            disabled={busy}
          />
          {open && suggestions.length > 0 && (
            <ul className="ut-depts-suggestions" role="listbox">
              {suggestions.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => openPreview(d)}
                    disabled={busy}
                  >
                    {d.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {open && query.trim().length > 0 && suggestions.length === 0 && (
            <div className="ut-depts-empty-results">No matching departments.</div>
          )}
        </div>
      )}

      {!readOnly && previewDept && (
        <div className="ut-depts-preview" role="alertdialog" aria-label="Confirm department add">
          <div>
            <strong>Add {previewDept.name}?</strong>
            <div className="ut-depts-preview-sub">
              {defaultsForPreview === null
                ? 'Loading default tool count…'
                : defaultsForPreview === 0
                  ? 'No default tools will be granted.'
                  : `${defaultsForPreview} default tool${defaultsForPreview === 1 ? '' : 's'} will be granted automatically.`}
            </div>
          </div>
          <div className="ut-depts-preview-actions">
            <button type="button" className="btn btn-small" onClick={cancelPreview} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-small btn-primary" onClick={commitAdd} disabled={busy}>
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
